/**
 * Utility functions for formatting and validating organization numbers
 * in the format ###-###-###-###
 */

/**
 * Strips all non-digit characters from an organization number
 * @param value - The input string that may contain dashes or other characters
 * @returns String containing only digits
 * @example
 * stripOrganizationNumberFormatting('123-456-789-012') // returns '123456789012'
 * stripOrganizationNumberFormatting('123 456 789 012') // returns '123456789012'
 */
export function stripOrganizationNumberFormatting(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Formats a string of digits into the organization number format ###-###-###-###
 * @param value - String containing digits (can include non-digits which will be stripped)
 * @returns Formatted string with dashes at positions 3, 6, and 9
 * @example
 * formatOrganizationNumber('123456789012') // returns '123-456-789-012'
 * formatOrganizationNumber('123456') // returns '123-456'
 * formatOrganizationNumber('123') // returns '123'
 */
export function formatOrganizationNumber(value: string): string {
  const digitsOnly = stripOrganizationNumberFormatting(value);

  if (digitsOnly.length === 0) {
    return '';
  }

  // Build the formatted string by inserting dashes at the right positions
  let formatted = '';
  for (let i = 0; i < digitsOnly.length; i++) {
    // Add dash after positions 3, 6, and 9
    if (i === 3 || i === 6 || i === 9) {
      formatted += '-';
    }
    formatted += digitsOnly[i];
  }

  return formatted;
}

/**
 * Validates that an organization number has exactly 12 digits
 * @param value - The input string to validate
 * @returns true if the stripped value contains exactly 12 digits
 * @example
 * isValidOrganizationNumber('123-456-789-012') // returns true
 * isValidOrganizationNumber('123456789012') // returns true
 * isValidOrganizationNumber('12345678901') // returns false
 */
export function isValidOrganizationNumber(value: string): boolean {
  const digitsOnly = stripOrganizationNumberFormatting(value);
  return digitsOnly.length === 12;
}
